import { useGetOrderById } from "@/hooks/seller/useGetOrderById"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useAuthStore } from "@/store/auth.store"
import { acceptPurchaseOrder, rejectPurchaseOrder, generateSellerInvoice, getSellerInvoiceUrl, getPurchaseOrderUrl, getPaymentReceiptUrl } from "@/lib/api/order.api"
import { useState } from "react"
import { toast } from "sonner"
import { CompanyDetailSidebar, ProductDetailSidebar, VariantDetailSidebar, ExternalSellerDetailSidebar, QuotationDetailSidebar } from "@/components/dashboard/shared/SourcingDetailSidebars"
import { TargetItemView } from "@/components/dashboard/shared/TargetItemView"
import { CommercialDocumentCard } from "@/components/dashboard/shared/CommercialDocumentCard"
import { useNavigate } from "@tanstack/react-router"
import { QUANTITY_UNITS } from "@/components/dashboard/seller/pages/rfqs/RfqDetailsPage"
import { useAddProductionStage } from "@/hooks/seller/useAddProductionStage"
import { useUpdateProductionStageStatus } from "@/hooks/seller/useUpdateProductionStageStatus"
import { useGetTrackingDetails } from "@/hooks/useGetTrackingDetails"
import TrackingDetailsDisplay from "@/components/dashboard/admin/components/TrackingDetailsDisplay"
import {
  Loader2,
  AlertCircle,
  ChevronLeft,
  FileText,
  Calendar,
  Package,
  Activity,
  Plus,
  CheckCircle2,
  Clock,
  Play,
  DollarSign,
  Link2,
  ShieldCheck,
  Truck,
  Zap,
  Plane,
  Ship,
  ArrowRight,
  ExternalLink,
  Eye,
  Download,
  Building2,
} from "lucide-react"

function formatDate(isoStr: string) {
  if (!isoStr) return "N/A"
  return new Date(isoStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

export function OrderDetailsPage({ orderId }: { orderId: string }) {
  const navigate = useNavigate()
  const handleGoBack = () => {
    if (window.history.length > 1) {
      window.history.back()
    } else {
      navigate({ to: "/orders" })
    }
  }

  const {
    data: orderDetailData,
    isLoading: isDetailLoading,
    isError: isDetailError,
    error: detailError,
  } = useGetOrderById(orderId, !!orderId)

  const selectedOrder = orderDetailData?.order || (orderDetailData && !orderDetailData.order && orderDetailData.id ? orderDetailData : null)

  if (isDetailLoading) {
    return (
      <div className="relative flex flex-col items-center justify-center py-20 text-slate-400 gap-3 bg-white border border-[#be001e]/20 rounded-2xl shadow-sm max-w-5xl mx-auto font-sans text-left min-h-[300px]">
        <div className="absolute top-5 left-5">
          <button
            onClick={handleGoBack}
            className="flex items-center justify-center p-2 rounded-lg border border-[#be001e]/20 bg-white hover:bg-[#be001e]/5 hover:text-[#be001e] hover:shadow-sm transition-all cursor-pointer text-slate-800"
            title="Back to Orders"
          >
            <ChevronLeft className="size-4" />
          </button>
        </div>
        <Loader2 className="size-8 animate-spin text-[#e6202f]" />
        <p className="text-sm font-medium text-slate-500 font-sans">Fetching order details...</p>
      </div>
    )
  }

  if (isDetailError) {
    return (
      <div className="max-w-5xl mx-auto space-y-4 font-sans text-left">
        <div className="flex items-center gap-3">
          <button
            onClick={handleGoBack}
            className="flex items-center justify-center p-2 rounded-lg border border-[#be001e]/20 bg-white hover:bg-[#be001e]/5 hover:text-[#be001e] transition-all cursor-pointer"
          >
            <ChevronLeft className="size-4" />
          </button>
          <span className="text-sm font-bold text-slate-700">Back to Orders</span>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-red-100 bg-red-50 p-5 text-sm text-[#e6202f]">
          <AlertCircle className="size-5 shrink-0" />
          <p className="font-semibold">{(detailError as Error)?.message || "Failed to load order details."}</p>
        </div>
      </div>
    )
  }

  if (!selectedOrder) {
    return (
      <div className="max-w-5xl mx-auto space-y-4 font-sans text-left">
        <div className="flex items-center gap-3">
          <button
            onClick={handleGoBack}
            className="flex items-center justify-center p-2 rounded-lg border border-[#be001e]/20 bg-white hover:bg-[#be001e]/5 hover:text-[#be001e] transition-all cursor-pointer"
          >
            <ChevronLeft className="size-4" />
          </button>
          <span className="text-sm font-bold text-slate-700">Back to Orders</span>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-yellow-100 bg-yellow-50 p-5 text-sm text-yellow-700">
          <AlertCircle className="size-5 shrink-0" />
          <p className="font-semibold">Order details not found.</p>
        </div>
      </div>
    )
  }

  return <OrderDetailView order={selectedOrder} onBack={handleGoBack} />
}

function OrderDetailView({ order, onBack }: { order: any; onBack: () => void }) {
  const q = order.quotation
  const charges = q?.adminToBuyerQuotationCharges || q?.sellerGeneratedQuotationCharges || q?.charges || []
  
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null)
  const [selectedCompany, setSelectedCompany] = useState<any | null>(null)
  
  const accessToken = useAuthStore((s) => s.accessToken)
  const queryClient = useQueryClient()

  const [showInvoiceForm, setShowInvoiceForm] = useState(false)
  const [invoiceUrl, setInvoiceUrl] = useState("")
  const [previewUrl, setPreviewUrl] = useState("")
  const [isGenerated, setIsGenerated] = useState(false)

  const generateInvoiceMutation = useMutation({
    mutationFn: () => {
      if (!accessToken) throw new Error("Not authenticated")
      return generateSellerInvoice(order.id, accessToken)
    },
    onSuccess: (res) => {
      const activeInvoiceUrl = res?.invoiceUrl || ""
      const activePreviewUrl = res?.previewUrl || res?.invoiceUrl || ""

      if (activeInvoiceUrl || activePreviewUrl) {
        setInvoiceUrl(activeInvoiceUrl)
        setPreviewUrl(activePreviewUrl)
        setIsGenerated(true)
      }
    },
  })

  const acceptPOMutation = useMutation({
    mutationFn: (sellerInvoiceUrl: string) => {
      if (!accessToken) throw new Error("Not authenticated")
      return acceptPurchaseOrder(order.id, sellerInvoiceUrl, accessToken)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["seller-order-detail", order.id] })
      setShowInvoiceForm(false)
      setInvoiceUrl("")
      setPreviewUrl("")
      setIsGenerated(false)
    },
  })

  const rejectPOMutation = useMutation({
    mutationFn: () => {
      if (!accessToken) throw new Error("Not authenticated")
      return rejectPurchaseOrder(order.id, accessToken)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["seller-order-detail", order.id] })
    },
  })
  const [selectedExternalSeller, setSelectedExternalSeller] = useState<any | null>(null)
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null)
  const [selectedQuotation, setSelectedQuotation] = useState<any | null>(null)

  const [activeTab, setActiveTab] = useState<"overview" | "commercial" | "production" | "shipment" | "payments" | "inspections">("overview")

  const [showForm, setShowForm] = useState(false)
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")

  const addStage = useAddProductionStage(order.id)
  const updateStage = useUpdateProductionStageStatus(order.id)

  const { data: trackingDetails, isLoading: isTrackingLoading } = useGetTrackingDetails(
    order.shipment?.trackingUrl,
    order.shipment?.trackingId
  )

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    addStage.mutate({ name, description }, {
      onSuccess: () => {
        setName("")
        setDescription("")
        setShowForm(false)
      }
    })
  }

  const stages = order.productionStages || []

  return (
    <div className="animate-in fade-in duration-200 text-left font-sans max-w-5xl mx-auto">
      <div className="group relative overflow-hidden bg-white border border-[#be001e]/20 hover:border-[#be001e]/30 rounded-2xl p-5 space-y-4 shadow-sm hover:shadow-[0_20px_50px_rgba(230,32,47,0.06)] transition-all duration-300">
        <div className="absolute top-0 left-0 right-0 h-[1px] group-hover:h-[3.5px] bg-gradient-to-r from-[#be001e] to-[#e6202f] rounded-t-2xl opacity-0 group-hover:opacity-100 transition-all duration-300" />

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
className="flex items-center gap-2 rounded-lg border border-[#be001e]/20 bg-white hover:bg-[#be001e]/5 hover:text-[#be001e] hover:shadow-sm transition-all cursor-pointer p-1"              
            >
              <ChevronLeft className="size-4" />
            </button>
            <span className="text-sm text-slate-700 font-medium ">Back to Orders</span>
          </div>

          <span className={`inline-flex items-center gap-1 rounded px-2.5 py-1 text-[10px] font-bold border uppercase tracking-wider ${
            order.status === "CONFIRMED"
              ? "bg-emerald-50 text-emerald-700 border-emerald-100"
              : order.status === "PENDING"
              ? "bg-amber-50 text-amber-700 border-amber-100"
              : order.status === "CANCELLED"
              ? "bg-red-50 text-red-700 border-red-100"
              : "bg-slate-50 text-slate-700 border-slate-200"
          }`}>
            {order.status}
          </span>
        </div>

        <div className="h-px bg-[#be001e]/10 w-full" />

        {/* Top Banner Alert for Pending PO Decision */}
        {(() => {
          const platformToSellerPO = order.purchaseOrders?.find((po: any) => po.type === "PLATFORM_TO_SELLER")
          const isPendingDecision = platformToSellerPO?.status === "PENDING"
          if (!isPendingDecision) return null

          return (
            <div
              onClick={() => {
                setActiveTab("overview")
                setTimeout(() => {
                  const el = document.getElementById("seller-next-action-card")
                  if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "center" })
                  }
                }, 50)
              }}
              className="flex items-center justify-between gap-3 rounded-xl border border-amber-300/80 bg-amber-50 hover:bg-amber-100/80 p-3 shadow-xs transition-all duration-200 cursor-pointer group animate-in fade-in slide-in-from-top duration-300"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="p-1.5 bg-amber-100 rounded-lg text-amber-700 shrink-0">
                  <Clock className="size-4 animate-pulse" />
                </div>
                <div className="min-w-0">
                  <h4 className="text-xs font-bold text-slate-800 flex items-center gap-2 font-sans truncate">
                    <span>Next Action: Awaiting Decision</span>
                    <span className="hidden sm:inline-block text-[9px] font-bold text-amber-800 bg-amber-200/80 px-2 py-0.5 rounded-full uppercase tracking-wider shrink-0">Action Required</span>
                  </h4>
                </div>
              </div>
              <div className="flex items-center gap-1 text-xs font-bold group-hover:translate-x-0.5 transition-transform shrink-0 font-sans">
                <ArrowRight className="size-3.5" />
              </div>
            </div>
          )
        })()}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3 space-y-4">
            <div className="flex overflow-x-auto gap-2 pb-2 border-b border-slate-200 scrollbar-hide">
              {[
                { id: "overview", label: "Overview" },
                { id: "commercial", label: "Invoices" },
                { id: "production", label: "Production" },
                { id: "shipment", label: "Shipment" },
                { id: "payments", label: "Payments" },
                { id: "inspections", label: "Inspections" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-t-lg transition-colors border-b-2 cursor-pointer whitespace-nowrap ${
                    activeTab === tab.id 
                      ? "text-[#be001e] border-[#be001e] bg-[#be001e]/5" 
                      : "text-slate-500 border-transparent hover:text-slate-800 hover:bg-slate-50"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="pt-2 space-y-4 min-h-[300px]">
            {activeTab === "commercial" && (
              <div className="space-y-6 animate-in fade-in duration-200">
                {/* 1. Purchase Orders Section */}
                <div className="space-y-3">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5 font-sans">
                    <FileText className="size-4 text-[#e6202f]" /> Purchase Orders
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(() => {
                      const sellerPos = (order.purchaseOrders || []).filter((po: any) => po.type === "PLATFORM_TO_SELLER")
                      if (sellerPos.length > 0) {
                        return sellerPos.map((po: any) => {
                          const title = "Purchase Order"
                          const fileName = po.documentUrl ? po.documentUrl.slice(po.documentUrl.lastIndexOf('/') + 1) : `PurchaseOrder_${po.id.slice(0, 8)}.pdf`
                          const statusType = po.status === "ACCEPTED" ? "success" : po.status === "PENDING" ? "warning" : "error"
                          return (
                            <CommercialDocumentCard
                              key={po.id}
                              title={title}
                              fileName={fileName}
                              status={po.status}
                              statusType={statusType}
                              date={formatDate(po.createdAt)}
                              onGetSignedUrl={() => getPurchaseOrderUrl(order.id, po.id, accessToken || "")}
                            />
                          )
                        })
                      }
                      return (
                        <CommercialDocumentCard
                          title="Purchase Order"
                          fileName="PurchaseOrder.pdf"
                          status="Pending Generation"
                          statusType="default"
                        />
                      )
                    })()}
                  </div>
                </div>

                {/* 2. Invoices Section */}
                {order.purchaseOrders && order.purchaseOrders.some((po: any) => po.sellerInvoiceUrl) && (
                  <div className="space-y-3">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5 font-sans">
                      <FileText className="size-4 text-[#e6202f]" /> Commercial Invoices
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Seller Commercial Invoices */}
                      {order.purchaseOrders && order.purchaseOrders
                        .filter((po: any) => po.sellerInvoiceUrl)
                        .map((po: any) => (
                          <CommercialDocumentCard
                            key={`invoice-${po.id}`}
                            title="Seller Commercial Invoice"
                            fileName={po.sellerInvoiceUrl.slice(po.sellerInvoiceUrl.lastIndexOf('/') + 1).split('?')[0]}
                            documentUrl={po.sellerInvoiceUrl}
                            status="Uploaded"
                            statusType="success"
                            date={formatDate(po.updatedAt || po.createdAt)}
                            onGetSignedUrl={() => getSellerInvoiceUrl(order.id, accessToken || "")}
                          />
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === "overview" && (
              <div className="space-y-4 animate-in fade-in duration-200">
                {/* Buyer Name */}
                {order.buyer?.user?.name && (
                  <div className="space-y-1.5">
                    <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1 font-sans">
                      <Building2 className="size-3.5 text-[#e6202f]" /> Buyer Name
                    </h4>
                    <div className="flex items-center justify-between gap-4 rounded-xl border border-[#be001e]/15 bg-slate-50/20 p-3 shadow-sm transition-all duration-200">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs text-slate-700 font-semibold tracking-tight font-sans">
                          {order.buyer.user.name}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Linked Quotation */}
                {q && (
                  <div className="space-y-1.5">
                    <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                      <FileText className="size-3.5 text-[#e6202f]" /> Linked Quotation
                    </h4>
                <div 
                  onClick={() => {
                    // Support both response formats:
                    // New format: q has message/adminToSellerRfq directly (no sellerToAdminQuotation)
                    // Old format: q.sellerToAdminQuotation is nested
                    if (q.sellerToAdminQuotation) {
                      setSelectedQuotation(q.sellerToAdminQuotation)
                    } else {
                      setSelectedQuotation(q)
                    }
                  }}
                  className="group/item flex items-center justify-between gap-4 rounded-xl border border-[#be001e]/15 bg-slate-50/40 hover:bg-slate-50 hover:border-[#be001e]/30 cursor-pointer p-3 shadow-sm transition-all duration-200"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-extrabold text-[#be001e] hover:underline text-xs tracking-tight font-mono">
                      {q.sellerToAdminQuotation
                        ? (q.sellerToAdminQuotation.message || "null")
                        : (q.message || "null")}
                    </span>
                  </div>
                  <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${
                    q.status === "APPROVED"
                      ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                      : q.status === "PENDING"
                      ? "bg-amber-50 text-amber-700 border-amber-100"
                      : "bg-red-50 text-red-700 border-red-100"
                  }`}>
                    {q.status}
                  </span>
                </div>
              </div>
            )}

            {(() => {
              // Support both response formats:
              // New format: q.adminToSellerRfq directly on q
              // Old format: q.sellerToAdminQuotation.adminToSellerRfq
              const sellerToAdminQuotation = q?.sellerToAdminQuotation
              const adminToSellerRfq = q?.adminToSellerRfq || sellerToAdminQuotation?.adminToSellerRfq
              const target = adminToSellerRfq?.targets?.[0]
              if (!target?.company && !target?.product) return null

              return (
                <div className="space-y-1.5">
                  <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                    <Package className="size-3.5 text-[#e6202f]" /> Sourcing Target Item(s)
                  </h4>
                  <TargetItemView
                    target={target}
                    onProductClick={(p) => setSelectedProductId(p.id)}
                    onCompanyClick={(c) => setSelectedCompany(c)}
                    onVariantClick={(v) => setSelectedVariantId(v.id)}
                    showSpecsGrid
                  />
                </div>
              )
            })()}

            {order.invoiceUrl && (
              <div className="space-y-1.5">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                  <FileText className="size-3.5 text-[#e6202f]" /> Invoice
                </h4>
                <div className="group/item flex items-center justify-between rounded-xl border border-[#be001e]/15 hover:border-[#be001e]/30 bg-slate-50/40 hover:bg-[#be001e]/[0.02] p-4 text-left shadow-sm transition-all duration-200">
                  <div className="flex items-center gap-3">
                    <div className="space-y-0.5">
                      <h4 className="font-extrabold text-slate-800 text-sm tracking-tight">Official Invoice</h4>
                    </div>
                  </div>
                  <a
                    href={order.invoiceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-widest text-[#be001e] bg-[#be001e]/5 hover:bg-[#be001e] hover:text-white px-3 py-1.5 rounded-lg transition-all duration-300"
                  >
                    View Invoice
                    <Link2 className="size-3" />
                  </a>
                </div>
              </div>
            )}

            {order.zipaWorldQuotation && (
              <div className="space-y-3 pt-2">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                  <Zap className="size-3.5 text-amber-500 fill-amber-500/20" /> Selected Logistics Rate (Zipaworld)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(() => {
                    const zq = order.zipaWorldQuotation
                    const isAir = zq.shipmentMode?.toUpperCase() === "AIR"
                    return (
                      <div 
                        className="relative overflow-hidden group/zq flex flex-col justify-between rounded-xl border border-slate-200/80 bg-white p-4.5 space-y-4"
                      >
                        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                          <div className="flex items-center gap-2">
                            <div className={`p-2 rounded-lg border ${
                              isAir 
                                ? "bg-sky-50 border-sky-100 text-sky-600" 
                                : "bg-blue-50 border-blue-100 text-blue-600"
                            }`}>
                              {isAir ? <Plane className="size-4" /> : <Ship className="size-4" />}
                            </div>
                            <div>
                              <h5 className="font-extrabold text-slate-800 text-[13px] leading-tight">
                                {zq.serviceName || "Standard"}
                              </h5>
                              <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">
                                {zq.quotationType?.replace(/_/g, " ") || "LOGISTICS RATE"}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded border uppercase tracking-wider text-slate-500 bg-slate-100 border-slate-200/50">
                              {zq.transitTime || "N/A"}
                            </span>
                          </div>
                        </div>

                        <div className="grid grid-cols-5 items-center gap-2 text-center py-1">
                          <div className="col-span-2 text-left min-w-0">
                            <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider block">ORIGIN</span>
                            <span className="font-bold text-slate-700 text-xs truncate block" title={zq.origin}>{zq.origin || "N/A"}</span>
                          </div>
                          <div className="col-span-1 flex flex-col items-center justify-center">
                            <ArrowRight className="size-3 text-slate-400" />
                          </div>
                          <div className="col-span-2 text-right min-w-0">
                            <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider block">DESTINATION</span>
                            <span className="font-bold text-slate-700 text-xs truncate block" title={zq.destination}>{zq.destination || "N/A"}</span>
                          </div>
                        </div>

                        <div className="bg-slate-50/50 border border-slate-200/60 rounded-xl p-3.5 text-xs font-semibold text-slate-600 space-y-2">
                          <div className="flex justify-between items-center text-[11.5px]">
                            <span className="text-slate-400">Freight Amount:</span>
                            <span className="font-mono text-slate-700">{zq.freightAmount || "N/A"}</span>
                          </div>
                          <div className="flex justify-between items-center text-[11.5px]">
                            <span className="text-slate-400">GST:</span>
                            <span className="font-mono text-slate-700">{zq.gst || "N/A"}</span>
                          </div>
                          <div className="h-px bg-slate-200/60 my-1" />
                          <div className="flex justify-between items-center text-xs font-bold">
                            <span className="text-slate-800">Total Amount:</span>
                            <span className="text-[#e6202f] font-mono font-black">{zq.totalAmount || "N/A"}</span>
                          </div>
                        </div>

                        {zq.viewRates && (
                          <div className="pt-1">
                            <a 
                              href={zq.viewRates} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="w-full h-9 border border-[#be001e]/20 text-[#be001e] hover:bg-[#be001e]/5 hover:border-[#be001e]/40 rounded-lg flex items-center justify-center gap-1.5 text-xs font-bold transition-all cursor-pointer shadow-sm"
                            >
                              <ExternalLink className="size-3.5" /> View Rates Details
                            </a>
                          </div>
                        )}
                      </div>
                    )
                  })()}
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1 font-sans">
                  <Package className="size-3.5 text-[#e6202f]" /> Charges Breakdown
                </h4>
              </div>
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm mt-3">
                {!q || !charges || charges.length === 0 ? (
                  <div className="p-8 text-center text-slate-400 italic text-xs">No charges specified</div>
                ) : (
                <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs whitespace-nowrap">
                  <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="p-3 min-w-[160px]">Description</th>
                      <th className="p-3 text-center w-32">Unit Price</th>
                      <th className="p-3 text-center w-24">Quantity</th>
                      <th className="p-3 text-center w-24">Unit</th>
                      <th className="p-3 text-center w-28">Discount (%)</th>
                      <th className="p-3 text-center w-24">Tax (%)</th>
                      <th className="p-3 text-center w-24">Tax Type</th>
                      <th className="p-3 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white">
                    {charges.map((charge: any) => {
                        const isAdvanced = charge.unitPrice !== undefined;
                        const desc = charge.description || "N/A"
                        const price = isAdvanced ? Number(charge.unitPrice || 0) : Number(charge.amount || 0)
                        const qty = isAdvanced ? ((charge.quantity !== null && charge.quantity !== undefined && charge.quantity !== "") ? Number(charge.quantity) : 1) : 1
                        const disc = isAdvanced ? Number(charge.discount || 0) : 0
                        const tax = isAdvanced ? Number(charge.tax || 0) : 0
                        
                        const base = price * qty
                        const sub = base - (base * disc / 100)
                        const lineTotal = sub + (sub * tax / 100)
                        const unitRaw = charge.measureUnit || charge.unit || charge.quantityUnit
                        const unitDisplay = isAdvanced && unitRaw ? (QUANTITY_UNITS.find((u) => u.code.toLowerCase() === String(unitRaw).toLowerCase() || u.name.toLowerCase() === String(unitRaw).toLowerCase())?.code || unitRaw) : "-"

                        return (
                          <tr key={charge.id || charge.description} className="hover:bg-slate-50/50 transition-colors">
                            <td className="p-3 text-slate-800 font-semibold">
                              {charge.productVariantId ? (
                                <button
                                  type="button"
                                  onClick={() => setSelectedVariantId(charge.productVariantId)}
                                  className="text-[#be001e] hover:text-[#e6202f] hover:underline text-left cursor-pointer transition-colors"
                                >
                                  {charge.description || "-"}
                                </button>
                              ) : (
                                charge.description || "-"
                              )}
                            </td>
                            <td className="p-3 text-center text-slate-600">{isAdvanced && charge.unitPrice !== null && charge.unitPrice !== undefined && charge.unitPrice !== "" ? charge.unitPrice : "-"}</td>
                            <td className="p-3 text-center text-slate-600">{isAdvanced && charge.quantity !== null && charge.quantity !== undefined && charge.quantity !== "" ? charge.quantity : "-"}</td>
                            <td className="p-3 text-center text-slate-600">{unitDisplay}</td>
                            <td className="p-3 text-center text-slate-600">{isAdvanced ? (charge.discount || 0) : "-"}</td>
                            <td className="p-3 text-center text-slate-600">{isAdvanced ? (charge.tax || 0) : "-"}</td>
                            <td className="p-3 text-center text-slate-600">
                              {isAdvanced && charge.taxType ? <span className="text-[9px] font-bold bg-slate-100 text-slate-500 px-1 py-0.5 rounded uppercase border border-slate-200">{charge.taxType}</span> : "-"}
                            </td>
                            <td className="p-3 text-right text-slate-900 font-bold font-mono">
                              {lineTotal.toFixed(2)}
                            </td>
                          </tr>
                        )
                      })
                    }
                  </tbody>
                </table>
                </div>
                )}
              </div>

              {q && (
                <div className="flex justify-end p-3 bg-white border border-slate-200 rounded-xl mt-3 shadow-sm">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mr-3 self-center">Grand Total:</span>
                  <span className="text-[12px] font-bold text-[#be001e] font-mono">
                    {(() => {
                      // Use order.totalAmount if available (old format),
                      // otherwise sum charges (new format)
                      if (order.totalAmount != null) {
                        return `${q.currencyCode ?? ""} ${Number(order.totalAmount).toFixed(2)}`
                      }
                      const computedTotal = charges.reduce((sum: number, c: any) => {
                        const isAdv = c.unitPrice !== undefined
                        const p = isAdv ? Number(c.unitPrice || 0) : Number(c.amount || 0)
                        const qVal = isAdv ? ((c.quantity !== null && c.quantity !== undefined && c.quantity !== "") ? Number(c.quantity) : 1) : 1
                        const disc = isAdv ? Number(c.discount || 0) : 0
                        const tax = isAdv ? Number(c.tax || 0) : 0
                        const base = p * qVal
                        const afterDiscount = base - (base * disc / 100)
                        return sum + afterDiscount + (afterDiscount * tax / 100)
                      }, 0)
                      return `${q.currencyCode ?? ""} ${computedTotal.toFixed(2)}`
                    })()}
                  </span>
                </div>
              )}

              {/* Next Action Card for Purchase Order decision */}
              {(() => {
                const platformToSellerPO = order.purchaseOrders?.find((po: any) => po.type === "PLATFORM_TO_SELLER")
                const isPendingDecision = platformToSellerPO?.status === "PENDING"
                if (!isPendingDecision) return null

                return (
                  <div id="seller-next-action-card" className="rounded-2xl border border-amber-200 bg-amber-50/50 p-5 mt-4 shadow-sm space-y-4 animate-in fade-in slide-in-from-bottom duration-300 scroll-mt-6">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-amber-100 rounded-xl text-amber-700 shrink-0">
                        <Clock className="size-5 animate-pulse" />
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-slate-600 leading-relaxed">
                          A Purchase Order has been issued by the platform. Please review the order details and charges above. You can either accept the order by uploading the commercial invoice or reject the order.
                        </p>
                      </div>
                    </div>

                    <div className="h-px bg-amber-200/60 w-full" />

                    <div className="overflow-hidden transition-all duration-300">
                      {!showInvoiceForm ? (
                        <div className="flex gap-3 justify-end">
                          <button
                            type="button"
                            onClick={() => {
                              rejectPOMutation.mutate()
                            }}
                            disabled={rejectPOMutation.isPending || acceptPOMutation.isPending}
                            className="h-9 px-4 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-bold transition active:scale-95 disabled:opacity-50 cursor-pointer animate-in fade-in duration-200"
                          >
                            {rejectPOMutation.isPending ? "Rejecting..." : "Reject"}
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowInvoiceForm(true)}
                            disabled={rejectPOMutation.isPending || acceptPOMutation.isPending}
                            className="h-9 px-4 rounded-lg bg-[#e6202f] hover:bg-[#be001e] text-white text-xs font-bold transition active:scale-95 disabled:opacity-50 cursor-pointer shadow-sm shadow-[#be001e]/20 animate-in fade-in duration-200"
                          >
                            Upload Invoice
                          </button>
                        </div>
                      ) : (
                        <form
                          onSubmit={(e) => {
                            e.preventDefault()
                            if (!invoiceUrl.trim()) return
                            acceptPOMutation.mutate(invoiceUrl)
                          }}
                          className="space-y-4 bg-white p-4 rounded-xl border border-slate-200/80 shadow-xs animate-in fade-in slide-in-from-top-2 duration-300"
                        >
                          <div className="space-y-1">
                            <h4 className="text-xs font-bold text-slate-800 tracking-tight">
                              Generate or attach an invoice for this order
                            </h4>
                            <div className="h-px bg-slate-100 w-full my-2" />
                          </div>

                          <div className="space-y-1.5">
                            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                              Invoice URL
                            </label>
                            <input
                              type="url"
                              placeholder="https://invoice.pdf"
                              value={invoiceUrl}
                              onChange={(e) => {
                                setInvoiceUrl(e.target.value)
                                setIsGenerated(false)
                              }}
                              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#be001e] focus:bg-white transition-colors"
                            />
                          </div>

                          {/* OR Divider */}
                          <div className="relative flex py-1 items-center">
                            <div className="flex-grow border-t border-slate-200" />
                            <span className="flex-shrink mx-3 text-[10px] font-extrabold uppercase text-slate-400 font-mono tracking-widest">
                              OR
                            </span>
                            <div className="flex-grow border-t border-slate-200" />
                          </div>

                          {/* Generate Invoice Action */}
                          <div className="flex items-center gap-3">
                            <button
                              type="button"
                              onClick={() => generateInvoiceMutation.mutate()}
                              disabled={generateInvoiceMutation.isPending || acceptPOMutation.isPending}
                              className="h-9 px-4 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-900 border border-amber-200/80 text-xs font-bold transition active:scale-95 disabled:opacity-50 cursor-pointer flex items-center gap-1.5 shadow-2xs"
                            >
                              {generateInvoiceMutation.isPending ? (
                                <>
                                  <Loader2 className="size-3.5 animate-spin text-amber-700" />
                                  <span>Generating...</span>
                                </>
                              ) : (
                                <>
                                  <Zap className="size-3.5 text-amber-600 fill-amber-500" />
                                  <span>Generate Invoice</span>
                                </>
                              )}
                            </button>

                            {(isGenerated || generateInvoiceMutation.isSuccess) && (
                              <span className="hidden sm:inline-flex items-center gap-1 text-[11px] font-extrabold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full animate-in fade-in duration-200">
                                <CheckCircle2 className="size-3.5 text-emerald-600" />
                                Generated ✓
                              </span>
                            )}
                          </div>

                          {/* Invoice Preview Section */}
                          {(previewUrl.trim() || invoiceUrl.trim()) && (
                            <div className="mt-3 p-3 bg-slate-50 rounded-xl border border-slate-200/80 space-y-2 animate-in fade-in duration-200">
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <div className="flex items-center gap-2 min-w-0">
                                  <div className="p-1.5 rounded-lg bg-[#be001e]/10 text-[#be001e] shrink-0">
                                    <FileText className="size-4" />
                                  </div>
                                  <div className="min-w-0">
                                    <span className="text-[10px] font-black uppercase tracking-wider text-slate-500 block">
                                      Invoice Document
                                    </span>
                                    <span className="text-xs font-bold text-slate-800 font-mono truncate max-w-[240px] sm:max-w-md block">
                                      {(previewUrl || invoiceUrl).split("/").pop()?.split("?")[0] || "Invoice.pdf"}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1.5 w-full sm:w-auto sm:justify-end">
                                  <a
                                    href={previewUrl || invoiceUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full sm:w-auto justify-center h-7 px-2.5 rounded-md border border-slate-200 bg-white hover:bg-slate-100 text-slate-700 text-[10px] font-bold flex items-center gap-1 transition-colors"
                                  >
                                    <Eye className="size-3" />
                                    <span>Preview</span>
                                    <ExternalLink className="size-2.5 text-slate-400" />
                                  </a>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Footer Controls */}
                          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                            <button
                              type="button"
                              onClick={() => {
                                setShowInvoiceForm(false)
                                setInvoiceUrl("")
                                setPreviewUrl("")
                                setIsGenerated(false)
                              }}
                              disabled={acceptPOMutation.isPending || generateInvoiceMutation.isPending}
                              className="h-9 px-4 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-bold transition active:scale-95 cursor-pointer"
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              disabled={acceptPOMutation.isPending || generateInvoiceMutation.isPending || !invoiceUrl.trim()}
                              className="h-9 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition active:scale-95 disabled:opacity-50 cursor-pointer shadow-xs flex items-center gap-1.5"
                            >
                              {acceptPOMutation.isPending ? (
                                <>
                                  <Loader2 className="size-3.5 animate-spin" />
                                  <span>Saving...</span>
                                </>
                              ) : (
                                <span>Save</span>
                              )}
                            </button>
                          </div>
                        </form>
                      )}
                    </div>
                  </div>
                )
              })()}
            </div>
          </div>
        )}

            {activeTab === "production" && (
              <div className="space-y-4 animate-in fade-in duration-200">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                      <Activity className="size-3.5 text-[#e6202f]" /> Production Stages
                    </h4>
                    <button
                      onClick={() => setShowForm(!showForm)}
                      className="flex items-center gap-1 text-[10px] font-bold text-[#e6202f] hover:text-[#be001e] transition-colors bg-[#e6202f]/5 hover:bg-[#e6202f]/10 border border-[#e6202f]/20 rounded-lg px-2.5 py-1 cursor-pointer"
                    >
                      <Plus className="size-3" /> Add Stage
                    </button>
                  </div>

                  {showForm && (
                    <form onSubmit={handleSubmit} className="bg-slate-50 border border-slate-100 rounded-xl p-4 space-y-3">
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Stage Name</label>
                        <input
                          type="text"
                          required
                          placeholder="e.g. Raw Material Sourcing"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#be001e] transition-colors"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Description</label>
                        <textarea
                          placeholder="e.g. Sourcing steel tubes from primary vendor"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          className="w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#be001e] transition-colors min-h-[60px]"
                        />
                      </div>
                      <div className="flex justify-end gap-2 pt-1">
                        <button
                          type="button"
                          onClick={() => setShowForm(false)}
                          className="text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:bg-slate-200 px-3 py-1.5 rounded-lg transition-colors cursor-pointer"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={addStage.isPending}
                          className="text-[10px] font-bold uppercase tracking-wider text-white bg-[#e6202f] hover:bg-[#be001e] px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 cursor-pointer"
                        >
                          {addStage.isPending ? "Adding..." : "Add Stage"}
                        </button>
                      </div>
                    </form>
                  )}

                  {stages.length === 0 ? (
                    <p className="text-xs text-slate-400 italic">No production stages added yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {stages.map((stage: any) => (
                        <div
                          key={stage.id}
                          className="flex flex-col sm:flex-row justify-between sm:items-center bg-slate-50/50 hover:bg-slate-50 border border-slate-100 rounded-xl p-4 gap-3 transition-colors"
                        >
                          <div className="space-y-1">
                            <h5 className="text-xs font-bold text-slate-800">{stage.name}</h5>
                            {stage.description && (
                              <p className="text-[11px] text-slate-500 leading-normal">{stage.description}</p>
                            )}
                            <span className="text-[9px] text-slate-400 block">
                              Added: {formatDate(stage.createdAt)}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-3 self-end sm:self-auto">
                            <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[9px] font-bold border uppercase tracking-wider ${
                              stage.status === "COMPLETED"
                                ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                                : stage.status === "IN_PROGRESS"
                                ? "bg-sky-50 text-sky-700 border-sky-100"
                                : "bg-amber-50 text-amber-700 border-amber-100"
                            }`}>
                              {stage.status === "COMPLETED" && <CheckCircle2 className="size-2.5" />}
                              {stage.status === "IN_PROGRESS" && <Play className="size-2.5 animate-pulse" />}
                              {stage.status === "PENDING" && <Clock className="size-2.5" />}
                              {stage.status}
                            </span>

                            <select
                              value={stage.status}
                              onChange={(e) => updateStage.mutate({ stageId: stage.id, status: e.target.value as any })}
                              className="text-[10px] font-bold uppercase tracking-wide border border-slate-200 rounded-lg px-2 py-1 bg-white text-slate-700 outline-none focus:border-[#be001e] transition-colors cursor-pointer"
                              disabled={updateStage.isPending}
                            >
                              <option value="PENDING">Pending</option>
                              <option value="IN_PROGRESS">In Progress</option>
                              <option value="COMPLETED">Completed</option>
                            </select>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === "payments" && (
              <div className="space-y-4 animate-in fade-in duration-200">
                {(() => {
                  const payments = (order.payments || (order.payment ? [order.payment] : [])).filter((p: any) => p.type === "ADMIN_TO_SELLER")
                  if (payments.length === 0) return <p className="text-xs text-slate-400 italic">No payments logged yet.</p>
                  return (
                    <div className="space-y-3">
                      <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                        <DollarSign className="size-3.5 text-[#e6202f]" /> Payments
                      </h4>
                      <div className="space-y-2">
                        {payments.map((payment: any) => (
                          <div
                            key={payment.id}
                            className="flex flex-col sm:flex-row justify-between sm:items-center bg-slate-50/50 border border-slate-100 rounded-xl p-4 gap-3 text-xs"
                          >
                            <div className="space-y-1 text-left">
                              <div className="flex items-center gap-2">
                                <span className={`font-bold text-[10px] tracking-wide uppercase px-1.5 py-0.5 rounded border ${
                                  payment.type === "BUYER_TO_ADMIN"
                                    ? "bg-blue-50 text-blue-700 border-blue-100"
                                    : "bg-indigo-50 text-indigo-700 border-indigo-100"
                                }`}>
                                  {payment.type.replace(/_/g, " ")}
                                </span>
                                <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-bold border uppercase tracking-wider ${
                                  payment.status === "COMPLETED"
                                    ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                                    : payment.status === "PENDING"
                                    ? "bg-amber-50 text-amber-700 border-amber-100"
                                    : payment.status === "FAILED"
                                    ? "bg-red-50 text-red-700 border-red-100"
                                    : "bg-slate-50 text-slate-700 border-slate-200"
                                }`}>
                                  {payment.status}
                                </span>
                              </div>
                              {payment.referenceId && (
                                <div className="flex items-center gap-1 text-slate-500 font-medium">
                                  <span className="font-semibold text-slate-400">Ref ID:</span>
                                  <span className="font-mono">{payment.referenceId}</span>
                                </div>
                              )}
                              {payment.referenceUrl && (
                                <div className="flex items-center gap-1 text-[#e6202f]">
                                  <Link2 className="size-3" />
                                  <button
                                    onClick={async () => {
                                      try {
                                        const res = await getPaymentReceiptUrl(order.id, payment.id, accessToken || "")
                                        if (res?.url) {
                                          window.open(res.url, "_blank", "noopener,noreferrer")
                                        }
                                      } catch (error) {
                                        console.error("Failed to get payment receipt URL:", error)
                                        toast.error("Failed to fetch payment receipt document")
                                      }
                                    }}
                                    className="hover:underline font-medium text-left cursor-pointer"
                                  >
                                    View Payment Receipt
                                  </button>
                                </div>
                              )}
                              <span className="text-[9px] text-slate-400 block">
                                Logged: {formatDate(payment.createdAt)}
                              </span>
                            </div>

                            <div className="text-right self-end sm:self-auto">
                              <span className="text-sm font-black text-slate-900 font-mono">
                                {q?.currencyCode ?? "—"} {payment.amount?.toLocaleString()}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })()}
              </div>
            )}

            {activeTab === "shipment" && (
              <div className="space-y-4 animate-in fade-in duration-200">
                <div className="space-y-3 text-left">
                  <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                    <Truck className="size-3.5 text-[#e6202f]" /> Shipment & Tracking
                  </h4>
                  {order.shipment ? (
                    <div className="space-y-3 bg-slate-50/40 border border-slate-100 rounded-xl p-4">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 text-xs pb-3 border-b border-slate-200/50">
                        <div>
                          <span className="text-slate-400 font-semibold block mb-0.5">Carrier Provider</span>
                          <span className="font-extrabold text-slate-800 uppercase">{order.shipment.provider}</span>
                        </div>
                        <div>
                          <span className="text-slate-400 font-semibold block mb-0.5">Tracking Number</span>
                          <span className="font-mono font-bold text-slate-800">{order.shipment.trackingId}</span>
                        </div>
                        <div>
                          <span className="text-slate-400 font-semibold block mb-0.5">Tracking Endpoint</span>
                          <a
                            href={order.shipment.trackingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#e6202f] hover:underline font-mono text-[10.5px] truncate block"
                            title={order.shipment.trackingUrl}
                          >
                            {order.shipment.trackingUrl}
                          </a>
                        </div>
                      </div>
                      {isTrackingLoading ? (
                        <div className="flex items-center justify-center py-6 gap-2 text-xs font-semibold text-slate-500">
                          <Loader2 className="size-4 animate-spin text-[#e6202f]" />
                          Loading live tracking updates...
                        </div>
                      ) : (
                        <TrackingDetailsDisplay trackingData={trackingDetails} />
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 italic">No shipment details available yet.</p>
                  )}
                </div>
              </div>
            )}

            {activeTab === "inspections" && (
              <div className="space-y-4 animate-in fade-in duration-200">
                {(() => {
                  const inspections = order.inspections || (order.inspection ? [order.inspection] : [])
                  if (inspections.length === 0) return <p className="text-xs text-slate-400 italic">No inspections logged yet.</p>
                  return (
                    <div className="space-y-3">
                      <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                        <ShieldCheck className="size-3.5 text-[#e6202f]" /> Inspections
                      </h4>
                      <div className="space-y-2">
                        {inspections.map((inspection: any) => (
                          <div
                            key={inspection.id}
                            className="flex flex-col gap-3 bg-slate-50/50 hover:bg-slate-50 border border-slate-100 rounded-xl p-4 transition-colors text-xs"
                          >
                            <div className="flex justify-between items-start gap-3">
                              <div className="space-y-1 text-left flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-slate-700">Inspection ID:</span>
                                  <span className="font-mono text-slate-800 bg-slate-100 px-1 py-0.5 rounded text-[10px]">
                                    {inspection.id.slice(0, 8).toUpperCase()}
                                  </span>
                                </div>
                                {inspection.message && (
                                  <p className="text-[11px] text-slate-600 font-semibold leading-relaxed">
                                    {inspection.message}
                                  </p>
                                )}
                                <span className="text-[9px] text-slate-400 block pt-1">
                                  Requested: {formatDate(inspection.createdAt)}
                                </span>
                              </div>
                              <div className="shrink-0">
                                <span className={`inline-flex items-center gap-1 rounded px-2.5 py-1 text-[10px] font-bold border uppercase tracking-wider ${
                                  inspection.status === "COMPLETED" || inspection.status === "PASSED" || inspection.status === "APPROVED"
                                    ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                                    : inspection.status === "PENDING"
                                    ? "bg-amber-50 text-amber-700 border-amber-100"
                                    : inspection.status === "FAILED" || inspection.status === "REJECTED"
                                    ? "bg-red-50 text-red-700 border-red-100"
                                    : "bg-slate-50 text-slate-700 border-slate-200"
                                }`}>
                                  {inspection.status}
                                </span>
                              </div>
                            </div>
                            {((inspection.notes && inspection.notes !== "string") || (inspection.documentUrl && inspection.documentUrl !== "string")) && (
                              <div className="bg-white border border-[#be001e]/15 rounded-xl p-3.5 space-y-2 shadow-[0_2px_8px_rgba(190,0,30,0.02)]">
                                <div className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-wider text-[#be001e] border-b border-slate-100 pb-1.5">
                                  <ShieldCheck className="size-3.5 shrink-0 text-[#e6202f]" />
                                  Inspection Report
                                </div>
                                {inspection.notes && inspection.notes !== "string" && (
                                  <p className="text-xs text-slate-600 font-semibold whitespace-pre-line leading-relaxed">
                                    {inspection.notes}
                                  </p>
                                )}
                                {inspection.documentUrl && inspection.documentUrl !== "string" && (
                                  <div className="flex items-center gap-1.5 pt-1 border-t border-dashed border-slate-100 mt-1">
                                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Report Doc:</span>
                                    <a
                                      href={inspection.documentUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-[#e6202f] hover:text-[#be001e] hover:underline font-extrabold font-mono text-[10.5px] flex items-center gap-1 transition-colors"
                                    >
                                      <Link2 className="size-3.5 shrink-0" />
                                      View Document
                                    </a>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })()}
              </div>
            )}
            </div>
          </div>

          <div className="lg:col-span-1 space-y-4 lg:border-l lg:border-[#be001e]/10 lg:pl-6 text-left">
            <div className="space-y-3">
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                <Calendar className="size-3.5 text-[#e6202f]" /> Timestamps
              </h4>
              <div className="relative pl-5 space-y-4">
                <div className="absolute left-[7px] top-1.5 bottom-1.5 w-[2px] bg-[#be001e]/15" />

                <div className="relative flex gap-2.5 items-start">
                  <div className="absolute -left-[23px] top-0.5 flex items-center justify-center size-[18px] rounded-full border border-sky-100 bg-[#e0f2fe]">
                    <div className="size-1.5 rounded-full bg-sky-600" />
                  </div>
                  <div className="space-y-0.5 min-w-0">
                    <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">ORDER DATE</span>
                    <p className="text-xs font-bold text-slate-800 leading-none">{formatDate(order.createdAt)}</p>
                  </div>
                </div>

                {/* {q && (
                  <div className="relative flex gap-2.5 items-start">
                    <div className="absolute -left-[23px] top-0.5 flex items-center justify-center size-[18px] rounded-full border border-red-100 bg-[#fee2e2]">
                      <div className="size-1.5 rounded-full bg-red-600" />
                    </div>
                    <div className="space-y-0.5 min-w-0">
                      <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">RFQ REFERENCE</span>
                      <p className="text-xs font-bold text-slate-800 leading-none font-mono">
                        #{q.rfqId?.slice(0, 8).toUpperCase() ?? "N/A"}
                      </p>
                    </div>
                  </div>
                )} */}
              </div>
            </div>
          </div>
        </div>
      </div>
      {selectedVariantId && (
        <VariantDetailSidebar
          variantId={selectedVariantId}
          onClose={() => setSelectedVariantId(null)}
          showCompanySpec={false}
        />
      )}
      {selectedProductId && (
        <ProductDetailSidebar
          productId={selectedProductId}
          onClose={() => setSelectedProductId(null)}
          showCompanySpec={false}
        />
      )}
      {selectedCompany && (
        <CompanyDetailSidebar 
          company={selectedCompany} 
          onClose={() => setSelectedCompany(null)} 
        />
      )}
      {selectedExternalSeller && (
        <ExternalSellerDetailSidebar 
          externalSeller={selectedExternalSeller} 
          onClose={() => setSelectedExternalSeller(null)} 
        />
      )}
      {selectedQuotation && (
        <QuotationDetailSidebar
          quotation={selectedQuotation}
          onClose={() => setSelectedQuotation(null)}
        />
      )}
    </div>
  )
}
